import { useDebounce } from 'react-use';
import { useEffect, useMemo, useState } from 'react';
import {
  useActiveProjectId,
  useNotify,
  useServiceAuth,
} from '../../../../../../hooks';
import SurveyService, {
  TestTemplateResponse,
} from '../../../../../../services/SurveyService';
import { useIntl, defineMessages } from 'react-intl';

const messages = defineMessages({
  testError: {
    id: 'UseTestTemplate.testError',
    defaultMessage: 'Error occurred testing template',
  },
});

export type UseTestTemplateArgs = {
  template?: string;
  response?: fhir.QuestionnaireResponse;
  surveyId: string;
  disableNotifications?: boolean;
};

export type UseTestTemplateReturnValue = {
  isFetching: boolean;
  isFetched: boolean;
  testResponse: TestTemplateResponse | null;
};

const surveyService = new SurveyService();

export const useTestTemplate = ({
  template,
  response,
  surveyId,
  disableNotifications = false,
}: UseTestTemplateArgs): UseTestTemplateReturnValue => {
  const serviceAuth = useServiceAuth();
  const activeProjectId = useActiveProjectId();
  const [testResponse, setTestResponse] = useState<TestTemplateResponse | null>(
    null,
  );
  const [isFetching, setIsFetching] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const compString = useMemo(
    () => JSON.stringify({ response, template }),
    [response, template],
  );
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const executeRequest = async () => {
    if (!response || !template) return;

    setIsFetching(true);
    try {
      const requestResponse = await surveyService.testSurveyTemplate(
        serviceAuth,
      )(activeProjectId, surveyId, response, template);
      setTestResponse(requestResponse.data);
      setIsFetched(true);
    } catch (e) {
      console.error(e);
      if (!disableNotifications) {
        notify(formatMessage(messages.testError), 'error');
      }
      setTestResponse(null);
    } finally {
      setIsFetching(false);
    }
  };

  useDebounce(
    () => {
      executeRequest();
    },
    500,
    [compString],
  );

  useEffect(() => {
    setTestResponse(null);
  }, [compString]);

  return {
    isFetching,
    testResponse,
    isFetched,
  };
};
